import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import OverlayLoader from "../Components/OverlayLoader";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";

import {
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  getUserNextFollowUpReport,
} from "../Services/APIService";

/* ===================== INTERFACES ===================== */

interface NextFollowupLead {
  leadId: number;
  leadName: string;
  phoneNumber: string;
  countryCode: string;
  email: string;
  leadStatus: string;
  source: string;
  subSource: string;
  leadCreatedOn: string;
  assignedUser: string;
  lastFollowUpStatus: string;
  followUpType: string;
  followUpDate: string;
  projectName: string;
}

interface FilterParams {
  tenantId: number;
  fromDate?: string;
  toDate?: string;
  sourceId?: number;
  subSourceId?: number;
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
}

interface SubMediaSource {
  mediaSourceID: number;
  name: string;
  id: number;
  isActive: boolean;
}

interface OptionType {
  value: number;
  label: string;
}

/* ===================== COMPONENT ===================== */

const NextFollowupDetails: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState<NextFollowupLead[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const [mediaSources, setMediaSources] = useState<MediaSource[]>([]);
  const [subMediaSources, setSubMediaSources] = useState<SubMediaSource[]>([]);
  const [filteredSubSources, setFilteredSubSources] = useState<SubMediaSource[]>([]);

  const tenantId = parseInt(localStorage.getItem("tenantId") || "1000");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");

  const [filterParams, setFilterParams] = useState<FilterParams>({
    tenantId,
    fromDate: "",
    toDate: "",
    sourceId: undefined,
    subSourceId: undefined,
  });

  const location = useLocation();

  /* ===================== INIT ===================== */

  useEffect(() => {
    fetchNextFollowupData();
    fetchDropdownData();
  }, []);

  /* ===================== DATA ===================== */

  const fetchNextFollowupData = async () => {
    try {
      setLoading(true);

      const res: any = await getUserNextFollowUpReport({
        TenantId: tenantId,
      });

      setLeads(res?.userNextFollowUp || []);
      console.log("Fetched leads:", res?.userNextFollowUp);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load next follow-up data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const sourceResponse = await fetchMeadiaSourceData();
      setMediaSources(sourceResponse.filter((s: MediaSource) => s.isActive));

      const subSourceResponse = await fetchAllSubMediaSource();
      setSubMediaSources(subSourceResponse.filter((s: SubMediaSource) => s.isActive));
    } catch (e) {
      console.error("Error fetching dropdown data:", e);
      toast.error("Failed to load filter options");
    }
  };

  /* ===================== FILTER ===================== */

  const formatDate = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    let fromDate: Date;
    let toDate: Date;

    switch (period) {
      case "today": {
        fromDate = new Date(year, month, today.getDate());
        toDate = new Date(year, month, today.getDate());
        break;
      }
      case "yesterday": {
        const y = new Date(year, month, today.getDate() - 1);
        fromDate = y;
        toDate = y;
        break;
      }
      case "thisMonth": {
        fromDate = new Date(year, month, 1);
        toDate = today;
        break;
      }
      case "lastMonth": {
        fromDate = new Date(year, month - 1, 1);
        toDate = new Date(year, month, 0);
        break;
      }
      case "last3Months": {
        fromDate = new Date(year, month - 3, 1);
        toDate = new Date(year, month, 0);
        break;
      }
      case "last6Months": {
        fromDate = new Date(year, month - 6, 1);
        toDate = new Date(year, month, 0);
        break;
      }
      default: {
        setFilterParams((prev) => ({
          ...prev,
          fromDate: "",
          toDate: "",
        }));
        return;
      }
    }

    setFilterParams((prev) => ({
      ...prev,
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    }));
  };

  const applyFilter = async () => {
    try {
      setLoading(true);

      const params: any = {
        TenantId: tenantId,
      };

      // FIX 1: Use correct parameter names matching the API service
      if (filterParams.fromDate) params.fromDate = filterParams.fromDate;
      if (filterParams.toDate) params.toDate = filterParams.toDate;
      if (filterParams.sourceId) params.sourceId = filterParams.sourceId;
      if (filterParams.subSourceId) params.subSourceId = filterParams.subSourceId;

      console.log("Applying filter with params:", params);

      const res: any = await getUserNextFollowUpReport(params);
      setLeads(res?.userNextFollowUp || []);
      toast.success("Filter applied successfully");
      console.log("Filtered leads:", res?.userNextFollowUp);
    } catch (e) {
      console.error("Error applying filter:", e);
      toast.error("Failed to apply filter");
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    setFilterParams({
      tenantId,
      fromDate: "",
      toDate: "",
      sourceId: undefined,
      subSourceId: undefined,
    });
    setSelectedPeriod("");
    setFilteredSubSources([]);
    fetchNextFollowupData();
  };

  /* ===================== SEARCH ===================== */

  const filteredLeads = leads.filter((l) => {
    const t = searchText.toLowerCase().trim();
    if (!t) return true;
    return (
      l.leadName.toLowerCase().includes(t) ||
      l.assignedUser.toLowerCase().includes(t) ||
      l.phoneNumber.includes(t)
    );
  });

  /* ===================== EXPORT ===================== */

  const exportToExcel = () => {
    try {
      const headers = [
        "Lead Name",
        "Phone",
        "Email",
        "Assigned User",
        "Follow-up Type",
        "Follow-up Date",
        "Status",
        "Source",
        "Sub Source",
        "Project",
        "Lead Status",
      ];

      const rows = filteredLeads.map((lead) => [
        lead.leadName,
        lead.phoneNumber,
        lead.email,
        lead.assignedUser,
        lead.followUpType,
        new Date(lead.followUpDate).toLocaleString(),
        lead.lastFollowUpStatus,
        lead.source,
        lead.subSource,
        lead.projectName || "-",
        lead.leadStatus,
      ]);

      let csvContent = headers.join(",") + "\n";
      rows.forEach((row) => {
        csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
      });

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `next-followup-details-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Excel file exported successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  const exportToPDF = () => {
    try {
      const bodyRows = filteredLeads
        .map(
          (lead) => `
            <tr>
              <td>${lead.leadName}</td>
              <td>${lead.countryCode}${lead.phoneNumber}</td>
              <td>${lead.assignedUser}</td>
              <td>${lead.followUpType}</td>
              <td>${new Date(lead.followUpDate).toLocaleString()}</td>
              <td class="status-cell">${lead.lastFollowUpStatus}</td>
              <td>${lead.projectName || "-"}</td>
            </tr>
          `
        )
        .join("");

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Next Follow-up Details Report</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              text-align: center;
              color: #333;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
              font-size: 12px;
            }
            th {
              background-color: #a78590;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .status-cell {
              text-align: center;
              font-weight: bold;
            }
            .meta-info {
              margin-bottom: 15px;
              font-size: 12px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Next Follow-up Details Report</h1>
          <div class="meta-info">
            <strong>Generated on:</strong> ${new Date().toLocaleString()}<br/>
            ${filterParams.fromDate
          ? `<strong>From Date:</strong> ${filterParams.fromDate}<br/>`
          : ""
        }
            ${filterParams.toDate
          ? `<strong>To Date:</strong> ${filterParams.toDate}<br/>`
          : ""
        }
            <strong>Total Records:</strong> ${filteredLeads.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>Lead Name</th>
                <th>Phone</th>
                <th>Assigned User</th>
                <th>Follow-up Type</th>
                <th>Follow-up Date</th>
                <th>Status</th>
                <th>Project</th>
              </tr>
            </thead>
            <tbody>
              ${bodyRows}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          toast.success("PDF print dialog opened");
        };
      } else {
        toast.error("Please allow pop-ups to export PDF");
      }
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  /* ===================== SOURCE CHANGE ===================== */

  const handleSourceOnChange = (sourceId: number | undefined) => {
    setFilterParams({
      ...filterParams,
      sourceId,
      subSourceId: undefined,
    });
  };

  useEffect(() => {
    if (filterParams.sourceId && subMediaSources.length > 0) {
      const filtered = subMediaSources.filter(
        (src) => src.mediaSourceID === filterParams.sourceId
      );
      setFilteredSubSources(filtered);
      if (!filtered.some((s) => s.id === filterParams.subSourceId)) {
        setFilterParams((prev) => ({
          ...prev,
          subSourceId: undefined,
        }));
      }
    } else {
      setFilteredSubSources([]);
      setFilterParams((prev) => ({
        ...prev,
        subSourceId: undefined,
      }));
    }
  }, [filterParams.sourceId, subMediaSources]);

  /* ===================== COLUMNS ===================== */
  const leadDashbordRedirection = async (leadId: number) => {
    try {
      navigate("/LeadDashboard", {
        state: { leadId },
      });
    } catch (error) {
      console.error("Error redirecting to lead details:", error);
    }
  };
  const columns = [
    {
      name: "Lead Name",
      selector: (row: NextFollowupLead) => row.leadName,
      sortable: true,
      width: "220px",
      cell: (row: NextFollowupLead) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "rgb(167, 133, 144)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {row.leadName?.charAt(0).toUpperCase() || "?"}
          </div>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              leadDashbordRedirection(row.leadId);
            }}
            style={{
              textDecoration: "none",
              fontWeight: 500,
              color: "#33475b",
            }}
          >
            {row.leadName || "N/A"}
          </a>
        </div>
      ),
    },
    {
      name: "Phone",
      cell: (row: NextFollowupLead) => (
        <span>
          {`${row.countryCode || ""} ${row.phoneNumber || ""}`.trim() || "N/A"}
        </span>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "Assigned User",
      selector: (row: NextFollowupLead) => row.assignedUser,
      sortable: true,
      width: "180px",
    },
    {
      name: "Follow-up Type",
      selector: (row: NextFollowupLead) => row.followUpType,
      sortable: true,
      width: "150px",
    },
    {
      name: "Follow-up Date",
      selector: (row: NextFollowupLead) => row.followUpDate,
      sortable: true,
      width: "200px",
      cell: (row: NextFollowupLead) => {
        const date = new Date(row.followUpDate);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        const formattedHours = String(hours).padStart(2, "0");
        return <span>{`${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`}</span>;
      },
    },
    {
      name: "Status",
      cell: (row: NextFollowupLead) => (
        <span
          className={`status-count-badge ${row.lastFollowUpStatus === "Completed" ? "status-5" : "status-6"
            }`}
        >
          {row.lastFollowUpStatus}
        </span>
      ),
      width: "150px",
    },
    {
      name: "Project",
      selector: (row: NextFollowupLead) => row.projectName || "-",
      sortable: true,
      width: "200px",
    },
  ];

  /* ===================== OPTIONS ===================== */

  const getSelectedOption = (options: OptionType[], value?: number) => {
    return options.find((option) => option.value === value) || null;
  };

  const sourceOptions: OptionType[] = mediaSources.map((s) => ({
    value: s.sourceId,
    label: s.sourceName,
  }));

  const subSourceOptions: OptionType[] = filteredSubSources.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  /* ===================== UI ===================== */

  return (
    <div className="wrapper">
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 10000 }}
      />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header carHeaderWithBtn">
                      <div className="card-title mb-0">
                        Next Follow-up Details Report
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <input
                          type="text"
                          className="p-2 datatableSearchInput"
                          placeholder="Search Lead / User / Phone"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ width: "250px" }}
                        />
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToPDF}
                          title="Export to PDF"
                        >
                          <i className="bi bi-file-pdf"></i> Export PDF
                        </button>
                        <button
                          className="demo-btn btn btn-outline-success btn-sm"
                          onClick={exportToExcel}
                          title="Export to Excel"
                        >
                          <i className="bi bi-file-excel"></i> Export Excel
                        </button>
                        <button
                          className="demo-btn btn btn-outline-primary btn-sm"
                          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                        >
                          <i
                            className={`bi bi-${isFilterExpanded ? "chevron-up" : "funnel"
                              }`}
                          ></i>{" "}
                          {isFilterExpanded ? "Hide Filters" : "Show Filters"}
                        </button>
                      </div>
                    </div>

                    {/* Filter Section */}
                    {isFilterExpanded && (
                      <div
                        className="card-body"
                        style={{
                          borderBottom: "1px solid #dee2e6",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        <div className="row g-3">
                          {/* <div className="col-md-3">
                            <label className="form-label">Period</label>
                            <select
                              className="form-select form-control-lg"
                              value={selectedPeriod}
                              onChange={(e) => handlePeriodChange(e.target.value)}
                            >
                              <option value="">Select Period</option>
                              <option value="today">Today</option>
                              <option value="yesterday">Yesterday</option>
                              <option value="thisMonth">This Month</option>
                              <option value="lastMonth">
                                Last Month (exclude this month)
                              </option>
                              <option value="last3Months">
                                Last 3 Months (exclude this month)
                              </option>
                              <option value="last6Months">
                                Last 6 Months (exclude this month)
                              </option>
                            </select>
                          </div> */}

                          <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.fromDate}
                              onChange={(e) =>
                                setFilterParams({
                                  ...filterParams,
                                  fromDate: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterParams.toDate}
                              onChange={(e) =>
                                setFilterParams({
                                  ...filterParams,
                                  toDate: e.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">Source</label>
                            <Select
                              options={sourceOptions}
                              value={getSelectedOption(
                                sourceOptions,
                                filterParams.sourceId
                              )}
                              onChange={(option) => handleSourceOnChange(option?.value)}
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder="Select Source"
                              isSearchable
                              isClearable
                            />
                          </div>

                          <div className="col-md-3">
                            <label className="form-label">Sub Source</label>
                            <Select
                              options={subSourceOptions}
                              value={getSelectedOption(
                                subSourceOptions,
                                filterParams.subSourceId
                              )}
                              onChange={(option) =>
                                setFilterParams({
                                  ...filterParams,
                                  subSourceId: option?.value,
                                })
                              }
                              className="basic-single-select"
                              classNamePrefix="select"
                              placeholder={
                                filterParams.sourceId
                                  ? "Select Sub Source"
                                  : "Select source first"
                              }
                              isSearchable
                              isClearable
                              isDisabled={!filterParams.sourceId}
                            />
                          </div>

                          <div
                            className="col-12"
                            style={{
                              display: "flex",
                              gap: "10px",
                              justifyContent: "flex-end",
                            }}
                          >
                            <button className="btn btn-secondary" onClick={clearFilter}>
                              Clear
                            </button>
                            <button className="btn btn-primary" onClick={applyFilter}>
                              Apply Filter
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="card-body p-0">
                      <DataTable
                        columns={columns}
                        data={filteredLeads}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        striped
                        responsive
                        noDataComponent="No next follow-up data found"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>

      <OverlayLoader
        loading={loading}
        message="Loading Next Follow-up Details Report"
        submessage="Please wait..."
      />

      <style>{`
        .rdt_TableCol:first-of-type {
          padding-left: 16px !important;
        }
        
        .status-count-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 13px;
          display: inline-block;
          transition: all 0.2s;
        }
        
        .status-count-badge:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        .status-1 {
          background: #ffebee;
          color: #c62828;
        }
        
        .status-2 {
          background: #fff3e0;
          color: #e65100;
        }
        
        .status-3 {
          background: #e3f2fd;
          color: #1976d2;
        }
        
        .status-4 {
          background: #f3e5f5;
          color: #7b1fa2;
        }
        
        .status-5 {
          background: #e0f2f1;
          color: #00695c;
        }
        
        .status-6 {
          background: #ffebee;
          color: #b71c1c;
        }
        
        .status-7 {
          background: #f5f5f5;
          color: #616161;
        }

        .css-1nmdiq5-menu {
          position: relative;
          z-index: 10000;
        }
      `}</style>
    </div>
  );
};

export default NextFollowupDetails;